<template>
  <ValidationObserver ref="basicDetails">
    <div class="mt-4">
      <ValidationProvider v-slot="{ errors }" name="jobTitle" rules="required">
        <v-text-field
          :error-messages="getErrorMessages(errors)"
          label="Job Title"
          class="mb-2 mt-4 pt-7"
          v-model="bookingTemplateData.jobTitle"
        />
      </ValidationProvider>
      <ValidationProvider v-slot="{ errors }" name="function" rules="required">
        <Select
          :errorMessages="getErrorMessages(errors)"
          autocomplete
          v-model="functionId"
          height="50"
          class="mt-2"
          label="Function"
          placeholder="Select Function"
          :items="functions"
          :loading="isFetchingFunctions"
          item-text="name"
          item-value="id"
        />
      </ValidationProvider>
      <div class="d-flex align-baseline flex-wrap">
        <v-text-field
          label="Salary"
          type="number"
          prefix="£"
          aria-label="Salary band lower"
          :value="salaryBandLower"
          @change="value => handleSalaryBandChange(value, 'salaryBandLower')"
        />
        <label class="mx-2">to</label>
        <v-text-field
          aria-label="Salary band upper"
          type="number"
          prefix="£"
          :value="salaryBandUpper"
          @change="value => handleSalaryBandChange(value, 'salaryBandUpper')"
        />
      </div>
      <ValidationProvider v-slot="{ errors }" name="grade" rules="required">
        <Select
          :errorMessages="getErrorMessages(errors)"
          autocomplete
          v-model="bookingTemplateData.grade"
          height="50"
          class="mt-2"
          label="Grade"
          placeholder="Select Grade"
          item-text="name"
          item-value="name"
          :items="grades"
          :loading="isFetchingGrades"
        />
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        name="contractType"
        rules="required"
      >
        <Select
          :errorMessages="getErrorMessages(errors)"
          autocomplete
          v-model="bookingTemplateData.contractType"
          height="50"
          class="mt-2"
          label="Contract Type"
          placeholder="Select Contract Type"
          :items="contractTypes"
          :loading="isFetchingContractTypes"
          item-text="originalName"
          item-value="originalName"
        />
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        name="workingHours"
        rules="required"
      >
        <Select
          :errorMessages="getErrorMessages(errors)"
          autocomplete
          v-model="bookingTemplateData.workingHours"
          height="50"
          class="mt-2"
          label="Working Hours"
          placeholder="Select Working Hours"
          :items="workingHours"
          :loading="isFetchingWorkingHours"
          item-text="name"
          item-value="name"
        />
      </ValidationProvider>
      <Select
        v-model="bookingTemplateData.complianceRequirements"
        :items="complianceRequirements"
        :loading="isFetchingComplianceRequirements"
        label="Compliance Requirements"
        multiple
      />
      <Select
        v-model="bookingTemplateData.otherRequirements"
        :items="otherRequirements"
        :loading="isFetchingOtherRequirements"
        label="Other Requirements"
        multiple
      />
      <v-label>Internal Description</v-label>
      <quill-editor
        v-model="bookingTemplateData.internalDescription"
        :options="editorOptions"
        class="mb-4 mt-2"
      />
      <v-label>External Description</v-label>
      <quill-editor
        v-model="bookingTemplateData.externalDescription"
        :options="editorOptions"
        class="mb-4 mt-2"
      />
      <h2 class="mb-4">Tags</h2>
      <ValidationProvider v-slot="{ errors }" rules="required">
        <Select
          v-model="publishingScheduleTag"
          :items="publishingScheduleTags"
          :loading="isFetchingPublishingScheduleTags"
          :errorMessages="getErrorMessages(errors)"
          label="Publishing Schedule"
        />
        <SelectedTemplates
          v-show="publishingScheduleTag"
          :tags="publishingScheduleTag ? [publishingScheduleTag] : []"
          type="publishingSchedule"
        />
      </ValidationProvider>
      <ValidationProvider v-slot="{ errors }" rules="required">
        <Select
          v-model="bookingTemplateData.messageTags"
          :items="messageTags"
          :loading="isFetchingMessageTags"
          label="Messages"
          multiple
          :errorMessages="getErrorMessages(errors)"
        />
        <SelectedTemplates
          v-show="size(bookingTemplateData.messageTags)"
          :tags="bookingTemplateData.messageTags || []"
          type="message"
        />
      </ValidationProvider>
      <ValidationProvider v-slot="{ errors }" rules="required">
        <Select
          v-model="bookingTemplateData.applicationQuestionTags"
          :items="applicationQuestionTags"
          :loading="isFetchingApplicationQuestionTags"
          label="Application Questions"
          multiple
          :errorMessages="getErrorMessages(errors)"
        />
        <SelectedTemplates
          v-show="size(bookingTemplateData.applicationQuestionTags)"
          :tags="bookingTemplateData.applicationQuestionTags || []"
          type="applicationQuestion"
        />
      </ValidationProvider>
      <ValidationProvider v-slot="{ errors }" rules="required">
        <Select
          v-model="bookingTemplateData.interviewFormatTemplateTags"
          :items="interviewFormatTemplateTags"
          :loading="isFetchingInterviewFormatTemplateTags"
          label="Interview Formats"
          multiple
          :errorMessages="getErrorMessages(errors)"
        />
        <SelectedTemplates
          v-show="size(bookingTemplateData.interviewFormatTemplateTags)"
          :tags="bookingTemplateData.interviewFormatTemplateTags || []"
          type="interviewFormat"
        />
      </ValidationProvider>
      <v-divider />
      <p>Application Profile Sections</p>
      <Checkbox
        @change="handleProfileSectionChange"
        :selected-values="initialProfileSectionData"
        :options="bookingOptions"
        isLarge
        mixed
      />
      <v-checkbox
        permission="edit.anonymise-applications"
        v-model="bookingTemplateData.anonymiseApplications"
        class="mt-0"
        label="Anonymise applications for roles created using this template"
      />
    </div>
  </ValidationObserver>
</template>

<script>
import Select from "@/components/common/Select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { merge, map, first, reduce, size } from "lodash";
import { createNamespacedHelpers, mapState } from "vuex";
import { DATA_NAMESPACE } from "@/store/modules/data";
import {
  FETCH_TAGS,
  FETCH_GRADES,
  FETCH_WORKING_HOURS,
  FETCH_FUNCTIONS,
  FETCH_CONTRACT_TYPES
} from "@/store/modules/data/actions";
import {
  GET_GRADES,
  GET_IS_FETCHING_GRADES,
  GET_WORKING_HOURS,
  GET_IS_FETCHING_WORKING_HOURS,
  GET_FUNCTIONS,
  GET_IS_FETCHING_FUNCTIONS,
  GET_CONTRACT_TYPES,
  GET_IS_FETCHING_CONTRACT_TYPES
} from "@/store/modules/data/getters";
import {
  getAmountInApiFormat,
  getAmountInDisplayFormat
} from "@/utils/helpers";
import { DEFAULT_TOOLBAR_OPTIONS } from "@/plugins/v-quill-editor";
import { BOOKING_OPTIONS } from "@/constants/bookings";
import Checkbox from "@/components/common/Checkbox";
import SelectedTemplates from "@/views/settings/components/role-templates/components/SelectedTemplates";

const { mapActions, mapGetters } = createNamespacedHelpers(DATA_NAMESPACE);

export default {
  components: {
    Select,
    ValidationProvider,
    ValidationObserver,
    Checkbox,
    SelectedTemplates
  },
  props: {
    bookingTemplate: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      bookingTemplateData: {
        jobTitle: "",
        externalDescription: "",
        internalDescription: "",
        publishingScheduleTags: [],
        contractType: "",
        grade: "",
        messageTags: [],
        applicationQuestionTags: [],
        otherRequirements: [],
        complianceRequirements: [],
        interviewFormatTemplateTags: [],
        anonymiseApplications: 0
      },
      functionId:
        this.bookingTemplate.function && this.bookingTemplate.function.id,
      showValidationErrors: false,
      editorOptions: {
        modules: {
          toolbar: {
            container: DEFAULT_TOOLBAR_OPTIONS
          }
        }
      },
      isFetchingPublishingScheduleTags: false,
      publishingScheduleTags: [],
      isFetchingApplicationQuestionTags: false,
      applicationQuestionTags: [],
      isFetchingMessageTags: false,
      messageTags: [],
      complianceRequirements: [],
      isFetchingComplianceRequirements: false,
      otherRequirements: [],
      isFetchingOtherRequirements: false,
      interviewFormatTemplateTags: [],
      isFetchingInterviewFormatTemplateTags: false,
      publishingScheduleTag: null,
      bookingOptions: BOOKING_OPTIONS
    };
  },
  created() {
    if (this.bookingTemplate) {
      this.adjustBookingTemplateData();
    }
    this.fetchAllTags();
    this.fetchGrades();
    this.fetchWorkingHours();
    this.fetchContractTypes();
    this.fetchFunctions();
    this.fetchComplianceRequirements();
    this.fetchOtherRequirements();
  },
  computed: {
    ...mapState("auth", {
      user: state => state.userData
    }),
    ...mapGetters({
      grades: GET_GRADES,
      isFetchingGrades: GET_IS_FETCHING_GRADES,
      workingHours: GET_WORKING_HOURS,
      isFetchingWorkingHours: GET_IS_FETCHING_WORKING_HOURS,
      functions: GET_FUNCTIONS,
      isFetchingFunctions: GET_IS_FETCHING_FUNCTIONS,
      contractTypes: GET_CONTRACT_TYPES,
      isFetchingContractTypes: GET_IS_FETCHING_CONTRACT_TYPES
    }),
    userOrganisationId() {
      return this.user.organisation && this.user.organisation.id;
    },
    salaryBandLower() {
      return (
        getAmountInDisplayFormat(this.bookingTemplateData.salaryBandLower) ||
        null
      );
    },
    salaryBandUpper() {
      return (
        getAmountInDisplayFormat(this.bookingTemplateData.salaryBandUpper) ||
        null
      );
    },
    initialProfileSectionData() {
      return [
        this.bookingTemplateData.requireCv && "requireCv",
        this.bookingTemplateData.requireExperience && "requireExperience",
        this.bookingTemplateData.requireQualifications &&
          "requireQualifications"
      ].filter(Boolean);
    }
  },
  methods: {
    ...mapActions({
      fetchTags: FETCH_TAGS,
      fetchGrades: FETCH_GRADES,
      fetchWorkingHours: FETCH_WORKING_HOURS,
      fetchFunctions: FETCH_FUNCTIONS,
      fetchContractTypes: FETCH_CONTRACT_TYPES
    }),
    adjustBookingTemplateData() {
      this.bookingTemplateData = merge({}, this.bookingTemplate);
      this.publishingScheduleTag = first(
        this.bookingTemplateData.publishingScheduleTags
      );
    },
    close() {
      this.$emit("close");
    },
    fetchAllTags() {
      this.fetchPublishingScheduleTags();
      this.fetchApplicationQuestionTags();
      this.fetchMessageTags();
      this.fetchInterviewFormatTemplateTags();
    },
    async fetchPublishingScheduleTags() {
      this.isFetchingPublishingScheduleTags = true;
      const { data: tags } = await this.fetchTags({
        filter: {
          taxonomy: "publishing_schedule"
        }
      });
      this.publishingScheduleTags = reduce(
        tags,
        (filteredTags, tag) => {
          if (tag.term.name !== "internal") {
            filteredTags.push(tag.term.name);
          }
          return filteredTags;
        },
        []
      );
      this.isFetchingPublishingScheduleTags = false;
    },
    async fetchApplicationQuestionTags() {
      this.isFetchingApplicationQuestionTags = true;
      const { data: tags } = await this.fetchTags({
        filter: {
          taxonomy: "application_questions"
        }
      });
      this.applicationQuestionTags = map(tags, "term.name");
      this.isFetchingApplicationQuestionTags = false;
    },
    async fetchMessageTags() {
      this.isFetchingMessageTags = true;
      const { data: tags } = await this.fetchTags({
        filter: {
          taxonomy: "message_templates"
        }
      });
      this.messageTags = map(tags, "term.name");
      this.isFetchingMessageTags = false;
    },
    async fetchInterviewFormatTemplateTags() {
      this.isFetchingInterviewFormatTemplateTags = true;
      const { data: tags } = await this.fetchTags({
        filter: {
          taxonomy: "interview_format_templates"
        }
      });
      this.interviewFormatTemplateTags = map(tags, "term.name");
      this.isFetchingInterviewFormatTemplateTags = false;
    },
    async fetchComplianceRequirements() {
      this.isFetchingComplianceRequirements = true;
      const { data: tags } = await this.fetchTags({
        filter: {
          taxonomy: "compliance_requirements"
        }
      });
      this.complianceRequirements = map(tags, "term.name");
      this.isFetchingComplianceRequirements = false;
    },
    async fetchOtherRequirements() {
      this.isFetchingOtherRequirements = true;
      const { data: tags } = await this.fetchTags({
        filter: {
          taxonomy: "other_requirements"
        }
      });
      this.otherRequirements = map(tags, "term.name");
      this.isFetchingOtherRequirements = false;
    },
    async validateForm() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.basicDetails.validate();
      if (isValid) {
        this.bookingTemplateData.client = { id: this.userOrganisationId };
        this.bookingTemplateData.function = { id: String(this.functionId) };
        this.bookingTemplateData.publishingScheduleTags = [
          this.publishingScheduleTag
        ];
        this.$emit("onSubmit", this.bookingTemplateData);
      }
      return isValid;
    },
    getErrorMessages(errors) {
      return this.showValidationErrors ? errors : "";
    },
    handleSalaryBandChange(value, salaryBand) {
      this.bookingTemplateData[salaryBand] = getAmountInApiFormat(value);
    },
    handleProfileSectionChange(values) {
      this.bookingTemplateData.requireCv = false;
      this.bookingTemplateData.requireExperience = false;
      this.bookingTemplateData.requireQualifications = false;
      map(values, value => {
        this.bookingTemplateData[value] = true;
      });
    },
    size
  },
  watch: {
    contractType(newValue) {
      this.bookingTemplateData.contractType = newValue;
    }
  }
};
</script>

<style lang="scss"></style>
